import "./css/adminstoredetail.css";
import { approveStore, rejectStore, getPendingStoreDetail } from "../../../utils/adminStoreService";
import { useState, useEffect, useRef } from "react";
import { IoClose } from "../../../components/icons";

const REJECT_REASON_MAX_LENGTH = 500;
const APPROVE_NOTE_MAX_LENGTH = 500;

const AdminStoreDetail = ({ store, onClose, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [storeDetail, setStoreDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState({
    url: "",
    name: "",
  });
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const rejectTextareaRef = useRef(null);
  const [showApproveForm, setShowApproveForm] = useState(false);
  const [approveNote, setApproveNote] = useState("");
  const approveTextareaRef = useRef(null);

  // 거절 사유 입력창이 열리면 스크롤 이동 + 자동 포커스
  useEffect(() => {
    if (showRejectForm && rejectTextareaRef.current) {
      rejectTextareaRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      rejectTextareaRef.current.focus();
    }
  }, [showRejectForm]);

  // 승인 메모 입력창이 열리면 스크롤 이동 + 자동 포커스
  useEffect(() => {
    if (showApproveForm && approveTextareaRef.current) {
      approveTextareaRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      approveTextareaRef.current.focus();
    }
  }, [showApproveForm]);

  // 상세 정보 조회
  useEffect(() => {
    const fetchStoreDetail = async () => {
      setDetailLoading(true);
      try {
        const result = await getPendingStoreDetail(store.storeCode);
        if (result.success) {
          setStoreDetail(result.data);
        } else {
          alert(result.error);
          onClose();
        }
      } catch (error) {
        alert("상세 정보를 불러오는 중 오류가 발생했습니다.");
        onClose();
      } finally {
        setDetailLoading(false);
      }
    };

    if (store.storeCode) {
      fetchStoreDetail();
    }
  }, [store.storeCode, onClose]);

  // 파일 타입 확인 함수
  const getFileType = (fileName) => {
    const extension = fileName.toLowerCase().split('.').pop();
    if (['pdf'].includes(extension)) return 'pdf';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)) return 'image';
    return 'other';
  };

  // 파일 클릭 핸들러
  const handleFileClick = (file) => {
    setSelectedImage({ url: file.fileUrl, name: file.originalName });
  };

  // 승인 메모 입력 폼 열기
  const handleApproveClick = () => {
    setShowApproveForm(true);
  };

  // 승인 메모 입력
  const handleApproveNoteChange = (e) => {
    setApproveNote(e.target.value.slice(0, APPROVE_NOTE_MAX_LENGTH));
  };

  // 승인 처리
  const handleApprove = async () => {
    if (!window.confirm("해당 가맹점을 승인하시겠습니까?")) {
      return;
    }

    setLoading(true);
    try {
      const result = await approveStore(store.storeCode, approveNote.trim());
      if (result.success) {
        alert(result.data || "성공적으로 승인하였습니다.");
        onClose();
        if (onRefresh) onRefresh(); // 목록 새로고침
      } else {
        alert(result.error || "승인 처리에 실패했습니다.");
      }
    } catch (error) {
      alert("승인 처리 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // 거절 사유 입력 폼 열기
  const handleRejectClick = () => {
    setShowRejectForm(true);
  };

  // 거절 사유 입력
  const handleRejectReasonChange = (e) => {
    setRejectReason(e.target.value.slice(0, REJECT_REASON_MAX_LENGTH));
  };

  // 거절 처리
  const handleReject = async () => {
    if (!rejectReason.trim()) {
      alert("거절 사유를 입력해주세요.");
      return;
    }

    if (!window.confirm("해당 가맹점을 거절하시겠습니까?")) {
      return;
    }

    setLoading(true);
    try {
      const result = await rejectStore(store.storeCode, rejectReason.trim());
      if (result.success) {
        alert(result.data || "성공적으로 거절되었습니다.");
        onClose();
        if (onRefresh) onRefresh(); // 목록 새로고침
      } else {
        alert(result.error || "거절 처리에 실패했습니다.");
      }
    } catch (error) {
      alert("거절 처리 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`adminstoredetail-backdrop ${selectedImage.url ? 'adminstoredetail-backdrop-image-view' : ''} ${selectedImage.name?.toLowerCase().endsWith('.pdf') ? 'adminstoredetail-backdrop-pdf' : ''}`}>
      <div
        className={`adminstoredetail-wrap ${selectedImage.name?.toLowerCase().endsWith('.pdf') ? 'adminstoredetail-wrap-pdf' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="adminstoredetail-header">
          <h2 className="adminstoredetail-title">가맹점 상세정보</h2>
          <button className="adminstoredetail-close-btn" onClick={onClose}>
            <IoClose />
          </button>
        </div>

        <div className="adminstoredetail-content">
          {detailLoading ? (
            <div className="adminstoredetail-loading">로딩 중...</div>
          ) : storeDetail ? (
            <>
              {/* 상호명 & 가맹타입 */}
              <div className="adminstoredetail-row">
                <label className="adminstoredetail-label">상호명</label>
                <div className="adminstoredetail-value">{storeDetail.storeNm || "-"}</div>
              </div>

              {/* 주소 */}
              <div className="adminstoredetail-row">
                <label className="adminstoredetail-label">주소</label>
                <div className="adminstoredetail-value">
                  {storeDetail.storeAddr || ""} {storeDetail.storeDetailAddr || ""}
                </div>
              </div>

              {/* 업체명 & 사업자등록일 */}
              <div className="adminstoredetail-row">
                <label className="adminstoredetail-label">업체명</label>
                <div className="adminstoredetail-value">{storeDetail.bizNm || "-"}</div>
                <label className="adminstoredetail-label adminstoredetail-label-inline">
                  사업자등록일
                </label>
                <div className="adminstoredetail-value adminstoredetail-value-inline">
                  {storeDetail.bizRegDate || "-"}
                </div>
              </div>

              {/* 사업자 구분 & 사업자 번호 */}
              <div className="adminstoredetail-row">
                <label className="adminstoredetail-label">사업자 구분</label>
                <div className="adminstoredetail-value">{storeDetail.bizType || "-"}</div>
                <label className="adminstoredetail-label adminstoredetail-label-inline">
                  사업자 번호
                </label>
                <div className="adminstoredetail-value adminstoredetail-value-inline">
                  {storeDetail.bizNo || "-"}
                </div>
              </div>

              {/* 대표자명 & 연락처 */}
              <div className="adminstoredetail-row">
                <label className="adminstoredetail-label">대표자명</label>
                <div className="adminstoredetail-value">{storeDetail.ownerNm || "-"}</div>
                <label className="adminstoredetail-label adminstoredetail-label-inline">
                  연락처
                </label>
                <div className="adminstoredetail-value adminstoredetail-value-inline">
                  {storeDetail.ownerTelNo || "-"}
                </div>
              </div>

              {/* 사업자 이미지 */}
              {storeDetail.bzImgs?.files?.length > 0 && (
                <div className="adminstoredetail-row adminstoredetail-row-full">
                  <label className="adminstoredetail-label">사업자 이미지</label>
                  <div className="adminstoredetail-images">
                    {storeDetail.bzImgs.files
                      .sort((a, b) => a.order - b.order)
                      .map((file, index) => {
                        const fileType = getFileType(file.originalName);
                        return (
                          <div
                            key={index}
                            className="adminstoredetail-image-item"
                            onClick={() => handleFileClick(file)}
                            style={{ cursor: 'pointer' }}
                          >
                            {fileType === 'pdf' ? (
                              <div className="adminstoredetail-file-pdf-icon">
                                <span className="adminstoredetail-file-pdf-text">PDF</span>
                              </div>
                            ) : (
                              <img
                                src={file.fileUrl}
                                alt={file.originalName}
                                className="adminstoredetail-image"
                              />
                            )}
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* 가맹점 대표 이미지 */}
              {storeDetail.storeImg?.files?.length > 0 && (
                <div className="adminstoredetail-row adminstoredetail-row-full">
                  <label className="adminstoredetail-label">가맹점 대표 이미지</label>
                  <div className="adminstoredetail-images">
                    {storeDetail.storeImg.files
                      .sort((a, b) => a.order - b.order)
                      .map((file, index) => {
                        const fileType = getFileType(file.originalName);
                        return (
                          <div
                            key={index}
                            className="adminstoredetail-image-item"
                            onClick={() => handleFileClick(file)}
                            style={{ cursor: 'pointer' }}
                          >
                            {fileType === 'pdf' ? (
                              <div className="adminstoredetail-file-pdf-icon">
                                <span className="adminstoredetail-file-pdf-text">PDF</span>
                              </div>
                            ) : (
                              <img
                                src={file.fileUrl}
                                alt={file.originalName}
                                className="adminstoredetail-image"
                              />
                            )}
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="adminstoredetail-error">상세 정보를 불러올 수 없습니다.</div>
          )}

          {showRejectForm && (
            <div className="adminstoredetail-row adminstoredetail-row-full">
              <label className="adminstoredetail-label">거절 사유</label>
              <textarea
                ref={rejectTextareaRef}
                className="adminstoredetail-reject-textarea"
                placeholder="거절 사유를 입력해주세요. 고객에게 전달될 내용이니 신중하게 작성해주세요."
                value={rejectReason}
                onChange={handleRejectReasonChange}
                maxLength={REJECT_REASON_MAX_LENGTH}
                disabled={loading}
              />
            </div>
          )}

          {showApproveForm && (
            <div className="adminstoredetail-row adminstoredetail-row-full">
              <label className="adminstoredetail-label">가맹점 메모</label>
              <textarea
                ref={approveTextareaRef}
                className="adminstoredetail-reject-textarea"
                placeholder="승인 시 남길 메모를 입력해주세요. (선택 입력)"
                value={approveNote}
                onChange={handleApproveNoteChange}
                maxLength={APPROVE_NOTE_MAX_LENGTH}
                disabled={loading}
              />
            </div>
          )}
        </div>

        <div className="adminstoredetail-footer">
          {showRejectForm ? (
            <>
              <button
                className="adminstoredetail-btn adminstoredetail-btn-reject"
                onClick={handleReject}
                disabled={loading}
              >
                {loading ? "처리 중..." : "거절 확정"}
              </button>
              <button
                className="adminstoredetail-btn adminstoredetail-btn-close"
                onClick={() => {
                  setShowRejectForm(false);
                  setRejectReason("");
                }}
                disabled={loading}
              >
                취소
              </button>
            </>
          ) : showApproveForm ? (
            <>
              <button
                className="adminstoredetail-btn adminstoredetail-btn-approve"
                onClick={handleApprove}
                disabled={loading}
              >
                {loading ? "처리 중..." : "승인 확정"}
              </button>
              <button
                className="adminstoredetail-btn adminstoredetail-btn-close"
                onClick={() => {
                  setShowApproveForm(false);
                  setApproveNote("");
                }}
                disabled={loading}
              >
                취소
              </button>
            </>
          ) : (
            <>
              <button
                className="adminstoredetail-btn adminstoredetail-btn-approve"
                onClick={handleApproveClick}
                disabled={loading}
              >
                승인
              </button>
              <button
                className="adminstoredetail-btn adminstoredetail-btn-reject"
                onClick={handleRejectClick}
                disabled={loading}
              >
                거부
              </button>
              <button
                className="adminstoredetail-btn adminstoredetail-btn-close"
                onClick={onClose}
                disabled={loading}
              >
                닫기
              </button>
            </>
          )}
        </div>
      </div>

      {/* 이미지/PDF 패널 */}
      <div className={`adminstoredetail-img ${selectedImage.name?.toLowerCase().endsWith('.pdf') ? 'adminstoredetail-img-pdf' : ''}`}>
        {selectedImage.url ? (
          <>
            {selectedImage.name?.toLowerCase().endsWith('.pdf') ? (
              <iframe 
                src={selectedImage.url}
                className="adminstoredetail-img-pdf-viewer"
                title={selectedImage.name}
              />
            ) : (
              <img 
                src={selectedImage.url} 
                alt={selectedImage.name}
                className="adminstoredetail-img-content"
              />
            )}
            <p className="adminstoredetail-img-name">{selectedImage.name}</p>
          </>
        ) : (
          <span className="adminstoredetail-img-placeholder">이미지를 선택하세요</span>
        )}
      </div>
    </div>
  );
};

export default AdminStoreDetail;